// src/pages/TechnicalReportFormPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { formatRut } from '../utils/formatters';
import { technicalReportService } from '../services/technicalReportService';
import imageCompression from 'browser-image-compression';

// Sub-formularios por tipo
import InformeGenericoForm from '../components/report-forms/InformeGenericoForm';
import InformeImagenesForm from '../components/report-forms/InformeImagenesForm';

const TYPE_NAMES = {
    generico: 'Informe Genérico',
    imagenes: 'Informe de Imágenes',
    verificacion_inicial: 'Verificación Inicial (TE1)',
    memoria_explicativa: 'Memoria Explicativa',
    pericial: 'Informe Pericial',
    calidad_energia: 'Fallas de Calidad Energética'
};

const TechnicalReportFormPage = ({ reportId, type: initialType, setPage, setNotification }) => {
    const { user } = useAuth();
    const { isAdmin, canCreate, canEdit } = usePermissions();
    const isEditMode = !!reportId;

    // Estado del formulario
    const [formData, setFormData] = useState({
        type: initialType || 'generico',
        titulo: '',
        clientName: '',
        clientId: '',
        ubicacion: '',
        fecha: new Date().toISOString().split('T')[0],
        sections: [{ titulo: '', contenido: '' }],
        antecedentes: '',
        conclusiones: '',
        recomendaciones: '',
        observaciones: '',
        descripcionGeneral: '',
        profesionalNombre: '',
        profesionalRut: '',
        profesionalTitulo: ''
    });

    const [photoPreviews, setPhotoPreviews] = useState([]);
    const [loading, setLoading] = useState(!!reportId);
    const [loadingMessage, setLoadingMessage] = useState('Guardar Informe');

    // Clientes
    const [allClients, setAllClients] = useState([]);
    const [clientNameSearch, setClientNameSearch] = useState('');
    const [clientSuggestions, setClientSuggestions] = useState([]);
    const [showClientSuggestions, setShowClientSuggestions] = useState(false);
    const clientSearchTimeout = useRef(null);

    // Fotos genéricas (para informe genérico)
    const fileInputRef = useRef(null);

    // Cargar datos iniciales
    useEffect(() => {
        const loadInit = async () => {
            try {
                const data = await technicalReportService.getInitialData();
                setAllClients(data.clients || []);
            } catch (err) { console.error(err); }
        };
        loadInit();
    }, []);

    // Cargar informe existente (modo edición)
    useEffect(() => {
        // Validación de permisos al entrar
        if (!isAdmin) {
            if (reportId && !canEdit('techReports')) {
                setNotification({ message: 'No tienes permiso para editar informes.', type: 'error' });
                setPage('techReports');
                return;
            }
            if (!reportId && !canCreate('techReports')) {
                setNotification({ message: 'No tienes permiso para crear informes.', type: 'error' });
                setPage('techReports');
                return;
            }
        }

        if (!reportId) return;
        const loadReport = async () => {
            setLoading(true);
            try {
                const data = await technicalReportService.getById(reportId);
                if (data) {
                    setFormData(data);
                    setClientNameSearch(data.clientName || '');
                    if (data.fotos) {
                        setPhotoPreviews(data.fotos.map(f => ({
                            src: f.url,
                            name: f.name,
                            file: null,
                            caption: f.caption || '',
                            description: f.description || ''
                        })));
                    }
                }
            } catch (err) {
                setNotification({ message: 'Error al cargar informe.', type: 'error' });
            } finally {
                setLoading(false);
            }
        };
        loadReport();
    }, [reportId, setNotification]);

    // Búsqueda de clientes
    const handleClientNameChange = (e) => {
        const name = e.target.value;
        setClientNameSearch(name);
        setFormData(prev => ({ ...prev, clientName: name }));
        if (clientSearchTimeout.current) clearTimeout(clientSearchTimeout.current);
        if (name.length >= 3) {
            clientSearchTimeout.current = setTimeout(() => {
                const term = name.toLowerCase();
                const matches = allClients.filter(c => c.nombre?.toLowerCase().includes(term)).slice(0, 10);
                setClientSuggestions(matches);
                setShowClientSuggestions(matches.length > 0);
            }, 100);
        } else {
            setShowClientSuggestions(false);
        }
    };

    const handleSelectClient = (c) => {
        setFormData(prev => ({
            ...prev,
            clientName: c.nombre,
            clientId: formatRut(c.rut || c.id),
            ubicacion: c.direccion || prev.ubicacion
        }));
        setClientNameSearch(c.nombre);
        setShowClientSuggestions(false);
    };

    // Fotos genéricas (para informe genérico)
    const handleGenericPhotoChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        setNotification({ message: 'Procesando imágenes...', type: 'info' });
        try {
            const compressed = await Promise.all(files.map(f => imageCompression(f, { maxSizeMB: 0.8, maxWidthOrHeight: 1280 })));
            const newPhotos = compressed.map(f => ({ src: URL.createObjectURL(f), name: f.name, file: f, caption: '', description: '' }));
            setPhotoPreviews(prev => [...prev, ...newPhotos]);
            setNotification({ message: `${files.length} imagen(es) agregada(s).`, type: 'success' });
        } catch (err) { console.error(err); }
        e.target.value = '';
    };

    // Guardar
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.titulo?.trim()) {
            setNotification({ message: 'El título del informe es obligatorio.', type: 'error' });
            return;
        }
        setLoading(true);
        setLoadingMessage('Guardando...');

        try {
            const newFiles = photoPreviews.filter(p => p.file).map(p => {
                const f = p.file;
                f.caption = p.caption || '';
                f.description = p.description || '';
                return f;
            });
            const existingInfos = photoPreviews.filter(p => !p.file).map(p => ({
                url: p.src,
                name: p.name,
                caption: p.caption || '',
                description: p.description || ''
            }));

            const savedId = await technicalReportService.save({
                reportId: isEditMode ? reportId : null,
                formData: { ...formData },
                photos: { newFiles, existingInfos },
                userId: user?.uid
            });

            setNotification({ message: 'Informe guardado con éxito.', type: 'success' });
            setTimeout(() => setPage('viewTechReport', savedId), 1000);
        } catch (err) {
            console.error(err);
            setNotification({ message: `Error: ${err.message}`, type: 'error' });
        } finally {
            setLoading(false);
            setLoadingMessage('Guardar Informe');
        }
    };

    if (loading && isEditMode) return <div className="p-8 text-center text-gray-600 dark:text-gray-400">Cargando...</div>;

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                        {isEditMode ? 'Editar Informe' : 'Nuevo Informe'}
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 font-semibold mt-1">
                        Tipo: {TYPE_NAMES[formData.type] || formData.type}
                    </p>
                </div>
                <button onClick={() => setPage('techReports')} className="text-blue-600 dark:text-blue-400 hover:underline">← Volver</button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 max-w-5xl mx-auto">
                {/* Datos del Cliente (Común a Todos) */}
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700">
                    <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">Datos del Cliente</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="relative">
                            <label className="block text-sm font-medium dark:text-gray-300">Cliente / Empresa</label>
                            <input type="text" value={clientNameSearch} onChange={handleClientNameChange} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="Buscar cliente..." />
                            {showClientSuggestions && (
                                <ul className="absolute z-10 w-full bg-white dark:bg-gray-700 border dark:border-gray-600 mt-1 max-h-40 overflow-y-auto shadow-lg rounded">
                                    {clientSuggestions.map(c => <li key={c.id} onClick={() => handleSelectClient(c)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-sm dark:text-gray-100">{c.nombre}</li>)}
                                </ul>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium dark:text-gray-300">RUT</label>
                            <input type="text" value={formData.clientId || ''} onChange={e => setFormData(prev => ({ ...prev, clientId: formatRut(e.target.value) }))} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium dark:text-gray-300">Dirección / Ubicación</label>
                            <input type="text" value={formData.ubicacion || ''} onChange={e => setFormData(prev => ({ ...prev, ubicacion: e.target.value }))} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                        </div>
                    </div>
                </div>

                {/* Renderizar formulario específico según tipo */}
                {formData.type === 'generico' && (
                    <InformeGenericoForm formData={formData} setFormData={setFormData} />
                )}
                {formData.type === 'imagenes' && (
                    <InformeImagenesForm
                        formData={formData}
                        setFormData={setFormData}
                        photoPreviews={photoPreviews}
                        setPhotoPreviews={setPhotoPreviews}
                        setNotification={setNotification}
                    />
                )}

                {/* Fotos genéricas (solo para informe genérico) */}
                {formData.type === 'generico' && (
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700">
                        <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">V. Registro Fotográfico</h2>
                        <button type="button" onClick={() => fileInputRef.current.click()} className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-4 py-2 rounded font-semibold mb-4 border dark:border-green-800">📷 Adjuntar Fotos</button>
                        <input type="file" ref={fileInputRef} onChange={handleGenericPhotoChange} multiple accept="image/*" className="hidden" />
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {photoPreviews.map((p, i) => (
                                <img key={i} src={p.src} alt="prev" className="w-full h-32 object-cover rounded border dark:border-gray-600" />
                            ))}
                        </div>
                    </div>
                )}

                {/* Firma del Profesional (Común) */}
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700">
                    <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">Firma del Profesional Responsable</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium dark:text-gray-300">Nombre Completo</label>
                            <input type="text" value={formData.profesionalNombre || ''} onChange={e => setFormData(prev => ({ ...prev, profesionalNombre: e.target.value }))} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="Ej: Juan Pérez González" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium dark:text-gray-300">RUT</label>
                            <input type="text" value={formData.profesionalRut || ''} onChange={e => setFormData(prev => ({ ...prev, profesionalRut: formatRut(e.target.value) }))} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="Ej: 12.345.678-9" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium dark:text-gray-300">Título / Profesión</label>
                            <input type="text" value={formData.profesionalTitulo || ''} onChange={e => setFormData(prev => ({ ...prev, profesionalTitulo: e.target.value }))} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="Ej: Ingeniero / Técnico en Electricidad" />
                        </div>
                    </div>
                </div>

                {/* Botón Guardar */}
                <div className="flex justify-end pt-4">
                    <button type="submit" disabled={loading} className="bg-blue-600 dark:bg-blue-700 text-white px-8 py-3 rounded-lg font-bold shadow-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:bg-gray-400 transition-colors">
                        {loading ? loadingMessage : 'Guardar Informe'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default TechnicalReportFormPage;
